/**
 * Focus Management Hooks
 * Utilities for managing focus in SPAs for accessibility
 *
 * WCAG Reference: 2.4.3 Focus Order
 */

import { useEffect, useLayoutEffect, useRef, useCallback, useState, RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * useFocusOnRouteChange
 * Manages focus when navigating between routes in SPA
 * Helps screen reader users understand that the page has changed
 *
 * @returns RefObject to attach to main content element
 *
 * @example
 * ```tsx
 * const MainLayout = () => {
 *   const mainRef = useFocusOnRouteChange();
 *   return (
 *     <main ref={mainRef} tabIndex={-1} id="main-content">
 *       <Outlet />
 *     </main>
 *   );
 * };
 * ```
 */
export const useFocusOnRouteChange = (): RefObject<HTMLElement> => {
  const location = useLocation();
  const mainContentRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip focus on initial render (user is already at top of page)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }

    // Focus main content on route change for screen readers
    if (mainContentRef.current) {
      // Small delay to ensure content is rendered
      const timeoutId = setTimeout(() => {
        mainContentRef.current?.focus({ preventScroll: false });
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [location.pathname]);

  return mainContentRef;
};

/**
 * useFocusReturn
 * Returns focus to the trigger element when a modal/dialog closes
 *
 * @param isOpen - Whether the modal is currently open
 * @returns Ref to attach to the trigger element
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useFocusReturn(isOpen);
 *
 * return (
 *   <>
 *     <Button ref={triggerRef} onClick={() => setIsOpen(true)}>
 *       Open Modal
 *     </Button>
 *     <Modal open={isOpen} onClose={() => setIsOpen(false)} />
 *   </>
 * );
 * ```
 */
export const useFocusReturn = <T extends HTMLElement>(isOpen: boolean): RefObject<T> => {
  const triggerRef = useRef<T>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    // When modal closes, return focus to trigger
    if (wasOpen.current && !isOpen && triggerRef.current) {
      triggerRef.current.focus();
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  return triggerRef;
};

/**
 * useAutoFocus
 * Automatically focus an element when mounted (for accessibility in dialogs)
 *
 * @param shouldFocus - Whether to focus the element (default: true)
 * @returns Ref to attach to the element to focus
 *
 * @example
 * ```tsx
 * const Dialog = () => {
 *   const focusRef = useAutoFocus<HTMLInputElement>();
 *   return <input ref={focusRef} />;
 * };
 * ```
 */
export const useAutoFocus = <T extends HTMLElement>(shouldFocus = true): RefObject<T> => {
  const elementRef = useRef<T>(null);

  useLayoutEffect(() => {
    if (shouldFocus && elementRef.current) {
      elementRef.current.focus();
    }
    return undefined;
  }, [shouldFocus]);

  return elementRef;
};

/**
 * useFocusWithin
 * Tracks whether focus is within a container element
 *
 * @returns [ref, hasFocusWithin]
 *
 * @example
 * ```tsx
 * const [containerRef, hasFocusWithin] = useFocusWithin<HTMLDivElement>();
 * return (
 *   <div ref={containerRef} style={{ outline: hasFocusWithin ? '2px solid blue' : 'none' }}>
 *     <input />
 *     <button>Submit</button>
 *   </div>
 * );
 * ```
 */
export const useFocusWithin = <T extends HTMLElement>(): [RefObject<T>, boolean] => {
  const containerRef = useRef<T>(null);
  const [hasFocusWithin, setHasFocusWithin] = useState(false);

  const checkFocusWithin = useCallback(() => {
    if (containerRef.current) {
      setHasFocusWithin(containerRef.current.contains(document.activeElement));
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = () => {
      setHasFocusWithin(true);
    };

    const handleFocusOut = (e: FocusEvent) => {
      // Check if focus moved outside container
      if (!container.contains(e.relatedTarget as Node)) {
        setHasFocusWithin(false);
      }
    };

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);

    // Initial check
    checkFocusWithin();

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, [checkFocusWithin]);

  return [containerRef, hasFocusWithin];
};
